"use client";

import { useState, useEffect } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

interface Testimonial {
  id: string;
  name: string;
  role: string;
  company: string;
  content: string;
  avatar: string;
  alt: string;
  rating: number;
}

export default function TestimonialsSection() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials: Testimonial[] = [
    {
      id: "test_1",
      name: "Sarah Mitchell",
      role: "Supply Chain Director",
      company: "TechCorp Electronics",
      content:
        "Ethical Minerals Uganda has transformed our sourcing strategy. Their transparency and certification process gives us complete confidence in our supply chain compliance.",
      avatar: "https://i.pravatar.cc/150?u=sarah",
      alt: "Sarah Mitchell professional headshot with business attire",
      rating: 5,
    },
    {
      id: "test_2",
      name: "James Anderson",
      role: "Chief Procurement Officer",
      company: "Global Manufacturing Inc",
      content:
        "The quality and consistency of their minerals are exceptional. We've been sourcing from them for 3 years and they've never let us down. Truly ethical and reliable.",
      avatar: "https://i.pravatar.cc/150?u=james",
      alt: "James Anderson professional headshot with confident expression",
      rating: 5,
    },
    {
      id: "test_3",
      name: "Maria Rodriguez",
      role: "ESG Compliance Manager",
      company: "Sustainable Industries",
      content:
        "Their commitment to fair trade and environmental standards aligns perfectly with our corporate values. The traceability documentation is comprehensive and audit-ready.",
      avatar: "https://i.pravatar.cc/150?u=maria",
      alt: "Maria Rodriguez professional headshot in business setting",
      rating: 5,
    },
  ];

  const nextTestimonial = () => {
    if (!isHydrated) return;
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    if (!isHydrated) return;
    setCurrentIndex(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
  };

  return (
    <section className="py-24 bg-background border-t border-border">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center items-center gap-4 mb-6">
            <div className="w-[2px] h-4 bg-gradient-to-b from-golden to-silver"></div>
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-transparent bg-clip-text bg-gradient-to-r from-golden to-silver font-mono">
              Client Testimonials
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-medium tracking-tight mb-4 font-heading">
            Trusted by Industry Leaders
          </h2>
          <p className="text-muted-foreground font-sans">
            See what our partners say about working with us
          </p>
        </div>

        {/* Testimonial Carousel */}
        <div className="relative">
          {isHydrated && (
            <div className="bg-card border-2 border-golden/30 rounded-2xl p-8 md:p-12 shadow-2xl shadow-golden/10 min-h-[400px] flex flex-col justify-between hover:border-golden/50 transition-all duration-500">
              {/* Quote Icon */}
              <div className="mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-golden/20 to-silver/20 rounded-full flex items-center justify-center">
                  <Icon
                    name="ChatBubbleLeftRightIcon"
                    size={32}
                    className="text-golden"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="flex-grow">
                <p className="text-xl md:text-2xl text-foreground leading-relaxed mb-8 font-sans">
                  "{testimonials[currentIndex].content}"
                </p>

                {/* Rating */}
                <div className="flex gap-1 mb-8">
                  {Array.from({
                    length: testimonials[currentIndex].rating,
                  }).map((_, i) => (
                    <Icon
                      key={`star_${i}`}
                      name="StarIcon"
                      size={20}
                      variant="solid"
                      className="text-golden"
                    />
                  ))}
                </div>
              </div>

              {/* Author */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-golden shadow-lg shadow-golden/30">
                    <AppImage
                      src={testimonials[currentIndex].avatar}
                      alt={testimonials[currentIndex].alt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-foreground font-heading">
                      {testimonials[currentIndex].name}
                    </h4>
                    <p className="text-sm text-muted-foreground font-sans">
                      {testimonials[currentIndex].role}
                    </p>
                    <p className="text-xs text-muted-foreground font-mono">
                      {testimonials[currentIndex].company}
                    </p>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex gap-2">
                  <button
                    onClick={prevTestimonial}
                    className="w-10 h-10 rounded-full border-2 border-golden/30 bg-background flex items-center justify-center hover:bg-gradient-to-br hover:from-golden hover:to-silver hover:text-foreground hover:border-golden transition-all"
                    aria-label="Previous testimonial"
                  >
                    <Icon name="ChevronLeftIcon" size={18} />
                  </button>
                  <button
                    onClick={nextTestimonial}
                    className="w-10 h-10 rounded-full border-2 border-golden bg-gradient-to-br from-golden to-silver text-foreground flex items-center justify-center hover:scale-110 transition-all shadow-lg"
                    aria-label="Next testimonial"
                  >
                    <Icon name="ChevronRightIcon" size={18} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Pagination Dots */}
          {isHydrated && (
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={`dot_${index}`}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-1 rounded-full transition-all ${
                    index === currentIndex
                      ? "w-8 bg-gradient-to-r from-golden to-silver shadow-lg" :"w-1 bg-border hover:bg-golden/50"
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}